/**
 * WAF monitors ("Monitoring CDN") — Owl/Mlytics CDN/availability monitors that
 * probe an origin through a specific CDN platform; a created monitor feeds the
 * Dynamic Data Feed for that platform. Customer-scoped (path: `/services/:service_id/`) — these
 * are account-wide, NOT anchored to a domain.
 *
 * Backed by `modules/service/waf/controller_monitors.go`:
 *   - GET    /services/:service_id/waf/monitors      → list (paginated; `name`, `status`, `sort_desc`)
 *   - POST   /services/:service_id/waf/monitors      → create
 *   - PUT    /services/:service_id/waf/monitors/:id  → full update (no `enable` field — Track is read-only)
 *   - DELETE /services/:service_id/waf/monitors/:id  → delete
 *
 * A monitor's probe shape is set by `type`: "managed" (default) probes an
 * origin/path; "custom" probes a full `url`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap, unwrapList, type ListResult } from './_shared';

/** The upstream a monitor probes. Type ∈ "FQDN" | "IPv4"; source is the host/IP. */
export interface WafMonitorOrigin {
  type?: string;
  source?: string;
}

/** One CDN/availability monitor. */
export interface WafMonitor {
  id: number;
  organization_id?: string;
  platform_name: string;
  asset_id?: string;
  name: string;
  /** Provisioning state, e.g. "completed" | "failed" | "in_progress". */
  status: string;
  is_cacheable: boolean;
  path?: string;
  /** Probe shape — e.g. "managed" | "custom". */
  type: string;
  enable: boolean;
  polls?: number;
  display_name?: string;
  zone_domain?: string;
  method?: string;
  created_at?: number;
  updated_at?: number;
}

/** List filter (all optional). */
export interface WafMonitorFilter {
  page?: number;
  page_size?: number;
  /** Substring filter on the monitor name. */
  name?: string;
  /** Provisioning-state CSV filter (e.g. "completed,failed"). */
  status?: string;
  sort_desc?: string;
  with_polls?: boolean;
}

/** Create body. `type` "managed" (default) uses origin/hostname/path; "custom" uses url. */
export interface WafMonitorCreate {
  platform_name: string;
  name: string;
  type?: string;
  origin?: WafMonitorOrigin;
  hostname?: string;
  is_cacheable?: boolean;
  path?: string;
  url?: string;
}

/** Update body — same as create minus `platform_name` (fixed at create). */
export type WafMonitorUpdate = Omit<WafMonitorCreate, 'platform_name'>;

/** List a customer's monitors (paginated). */
export function useWafMonitors(
  serviceId: string | undefined,
  filter: WafMonitorFilter = {},
  enabled = true,
) {
  const params = { ...filter };
  return useQuery<ListResult<WafMonitor>>({
    queryKey: wafKeys.monitors(serviceId ?? '', filter as Record<string, unknown>),
    enabled: !!serviceId && enabled,
    staleTime: 15_000,
    queryFn: async () =>
      unwrapList<WafMonitor>(await serviceApiV3.get(`/services/${serviceId}/waf/monitors`, { params })),
  });
}

/** Create a monitor (`POST /waf/monitors`). */
export function useCreateWafMonitor(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafMonitor, Error, WafMonitorCreate>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: WafMonitorCreate) =>
      unwrap<WafMonitor>(
        await serviceApiV3.post(`/services/${serviceId}/waf/monitors`, input),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'monitors'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a monitor (`PUT /waf/monitors/:id`). */
export function useUpdateWafMonitor(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafMonitor, Error, { id: number; input: WafMonitorUpdate }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: WafMonitorUpdate }) =>
      unwrap<WafMonitor>(
        await serviceApiV3.put(`/services/${serviceId}/waf/monitors/${id}`, input),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'monitors'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a monitor (`DELETE /waf/monitors/:id`). */
export function useDeleteWafMonitor(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { id: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      unwrap<unknown>(
        await serviceApiV3.delete(`/services/${serviceId}/waf/monitors/${id}`),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'monitors'] });
      options?.onSuccess?.(...args);
    },
  });
}
